import { Component, OnDestroy, OnInit, NgZone } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
    TCI_APP_MONITORING_REQUEST_TYPE,
    ITCIApplication,
    ITCIMetricsStatus,
    ITCIApplicationMonitoringProvider,
    ITCIMonitoringInstance,
    ITCIApplicationMonitoring,
    ITCITenantAppDetailMonitoringRanges,
    TCITenantSelectorType,
    TCIApplicationMonitoringMode,
    TCITenantAppDetailMonitoringRanges
} from "@tci/common";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../../../ng-app-details-menu";
import { AbstractTCITenantComponent } from "../../../../../ng-tenant/tci-tenant-component";
import { NgToastrService } from "../../../../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { BaseSubmenuMonitoringResourceMetricsService, ITCIResourceData } from "./base-submenu-resource-metrics.service";
import { NgToolsService } from "../../../../../ng-shared/ng-util/ng-tools.service";

import { setTimeoutInZone } from "../../../../../ng-shared/ng-util/tools";
import { Subscription, Subject } from "rxjs";
import * as lodash from "lodash";
import * as moment from "moment";

interface IMetricsRange extends ITCITenantAppDetailMonitoringRanges {
    active: boolean;
}

@Component({
    selector: TCITenantSelectorType.TCI_MENU_APP_MONITORING_RESOURCE_METRICS.toString(),
    templateUrl: "./base-submenu-resource-metrics.component.html",
    styleUrls: ["./base-submenu-resource-metrics.component.less"]
})
export class BaseSubmenuResourceMetricsComponent<E, I extends BaseAppDetailMenuComponentInput,
    O extends BaseAppDetailMenuComponentOutput<E>> extends AbstractTCITenantComponent<E, I, O> implements OnInit, OnDestroy {
    app: ITCIApplication;
    mode = TCIApplicationMonitoringMode.MODE_APP_DETAILS;
    cpu: { name: string, value: number };
    memory: { name: string, value: number };

    cpuColorScheme = { domain: ["#6beab7"] };
    cpuChart = {
        view: undefined,
        colorScheme: this.cpuColorScheme,
        schemeType: "ordinal",
        gradient: false,
        showXAxis: true,
        showYAxis: true,
        showLegend: false,
        showXAxisLabel: false,
        showYAxisLabel: false,
        xAxisLabel: "Time",
        yAxisLabel: "",
        xAxisTickFormatting: this.xAxisTickFormatting,
        yAxisTickFormatting: this.yAxisTickFormatting1,
        autoScale: true,
        timeline: false,
        showGridLines: true,
        rangeFillOpacity: 0.15,
        roundDomains: false,
        tooltipDisabled: false,
        curve: "monotone"
    };

    // execColorScheme = { domain: ["#86134f", "#5e99d6"] };
    memoryChart = lodash.assign(lodash.cloneDeep(this.cpuChart), { colorScheme: { domain: ["#fd8b83"] } });
    execSuccChart = lodash.assign(lodash.cloneDeep(this.cpuChart), { colorScheme: { domain: ["#6beab7"] } });
    execFailChart = lodash.assign(lodash.cloneDeep(this.cpuChart), { colorScheme: { domain: ["#fd8b83"] } });

    // Monitor provider
    private provider: ITCIApplicationMonitoringProvider;

    // Input for cpu+memory stats component
    statsData: { cpu: Subject<ITCIMetricsStatus[]>, memory: Subject<ITCIMetricsStatus[]> };

    // Subscriptions collection
    private subscriptions: Subscription[] = [];

    // Data for charts
    cpuChartData$: Subject<ITCIResourceData[]>;
    memoryChartData$: Subject<ITCIResourceData[]>;
    execSuccChartData$: Subject<ITCIResourceData[]>;
    execFailChartData$: Subject<ITCIResourceData[]>;

    // For selected display
    showChart = { cpu: true, memory: true, execSucc: false, execFail: false };
    selected = 0;

    // Refresh
    animateRefeshIcon = false;
    selectedInstance: ITCIMonitoringInstance;

    // IPAS-13413
    canShowExecutionsSection = true;

    // IPAS-13790 Time range
    timeRangeOptions: IMetricsRange[] = (() => {
        const opts: IMetricsRange[] = [];
        const ranges = TCITenantAppDetailMonitoringRanges.getAllRanges();
        const defRange = TCITenantAppDetailMonitoringRanges.getDefault();
        // tslint:disable-next-line: forin
        for (const i in ranges) {
            const active = ranges[i].id === defRange.id;
            opts.push(Object.assign(ranges[i], { active }));
        }
        return opts;
    })();

    constructor(
        public ngZone: NgZone,
        protected resourceSvc: BaseSubmenuMonitoringResourceMetricsService,
        protected translate: TranslateService,
        protected toastr: NgToastrService,
        protected toolsSvc: NgToolsService
    ) {
        super();

        this.statsData = { cpu: this.resourceSvc.getCPUStatsSubject(), memory: this.resourceSvc.getMemoryStatsSubject() };
        this.cpuChartData$ = this.resourceSvc.getCpuChartSubject();
        this.memoryChartData$ = this.resourceSvc.getMemoryChartSubject();
        this.execSuccChartData$ = this.resourceSvc.getExecSuccChartSubject();
        this.execFailChartData$ = this.resourceSvc.getExecFailChartSubject();
    }

    ngOnInit() {
        this.resourceSvc.getTenantMonitorProvider(this.input.getApplication())
            .subscribe(provider => {
                this.app = this.input.getApplication();
                this.provider = provider;

                const instanceReact = (res: ITCIMonitoringInstance) => {
                    this.resourceSvc.getNgZone().run(() => {
                        this.animateRefeshIcon = false;
                        if (res) {
                            this.selectedInstance = res;
                            const rangeId = this.timeRangeOptions.find(opt => opt.active).id; // Get time range
                            this.resourceSvc.initializeData(this.app, this.selectedInstance, rangeId);
                        } else { // No instances available
                            delete this.selectedInstance;
                        }
                        this.select(this.selected);
                    });
                };

                this.provider.getSelectedInstance()
                    .filter(inst => !!inst)
                    .subscribe(res => instanceReact(res));

                this.subscriptions.push(
                    this.provider.getAppMonitoringSubject(TCI_APP_MONITORING_REQUEST_TYPE.APP_INSTANCE_SELECTED)
                        .map(res => (<ITCIApplicationMonitoring>res.result).selectedInstance)
                        .subscribe(res => instanceReact(res))
                );
            });

        this.canShowExecutionsSection = this.resourceSvc.canShowExecutionsSection(this.app);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    select(v: number) {
        this.selected = v;
        this.showChart.cpu = !!this.selectedInstance?.key && v === 0;
        this.showChart.memory = !!this.selectedInstance?.key && v === 0;
        this.showChart.execSucc = !!this.selectedInstance?.key && v === 1;
        this.showChart.execFail = !!this.selectedInstance?.key && v === 1;
    }

    yAxisTickFormatting1(val: number): string {
        return val.toString() + "%";
    }

    xAxisTickFormatting(val: string): string {
        return moment(val).format("H:mm:ss");
    }

    refresh() {
        const rangeId = this.timeRangeOptions.find(opt => opt.active).id;
        this.resourceSvc.initializeData(this.app, this.selectedInstance, rangeId);
        if (this.ngZone && this.ngZone.run) {
            this.animateRefeshIcon = true;
            const cb = () => {
                this.animateRefeshIcon = false;
            };
            setTimeoutInZone(this.ngZone, cb, 2000);
        }
    }

    selectRange(optId: number) {
        this.timeRangeOptions.forEach(tr => tr.active = tr.id === optId);
        this.refresh();
    }
}
